import {
  ListItem,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  getPropertyGroupRulesets,
  getPropertyRulesets,
  getTenantRules,
} from 'api';
import {
  createRuleset,
  getTenantRulesets,
  putPropertyGroupRulesets,
  putPropertyRulesets,
  updateRuleset,
} from 'api/rulesets';
import MobilePanelList from 'components/lists/MobilePanelList';
import { TAB_LAYOUT_HEADER_HEIGHT } from 'components/layout/FormTabNavigation';
import { ACTION_TYPE, AppContext } from 'context/appContext';
import { TOAST_TYPE, ToastContext } from 'context/toastContext';
import React, {
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  ActionFunction,
  Outlet,
  useFetcher,
  useLoaderData,
  useLocation,
  useNavigate,
  useOutletContext,
  useParams,
} from 'react-router';
import { ROUTES, routeWithParams } from 'router';
import {
  DisplayMode,
  EntityPanelActionHandler,
  EntityTypes,
  FormMode,
  PanelRootContext,
  Property,
  PropertyGroup,
  Rule,
  Ruleset,
  Status,
  Tenant,
  TraxeroActionResponse,
} from 'types';
import { capitalize, getBlankRuleset } from 'utils';
import AuthService from 'utils/AuthService';
import {
  MaterialReactTable,
  useMaterialReactTable,
  type MRT_ColumnDef,
  MRT_SortingState,
  MRT_Row,
} from 'material-react-table';
import { Add, Delete, History, Warning } from '@mui/icons-material';
import EntityLookup from 'components/autocompletes/EntityLookup';
import {
  TableType,
  useDefaultTableOptions,
} from 'hooks/useDefaultTableOptions';
import dayjs from 'dayjs';
import MRTableToolbar from 'components/tables/PanelTableToolbar';
import { ListItemMenuButton } from 'components/buttons/ListItemMenuButton';
import { useFetcherKey } from 'hooks/useFetcherKey';
import FormFrame from 'components/layout/FormFrame';

export interface RulesetLoaderData {
  _tenantRules: Promise<Rule[]>;
  _rulesets: Promise<Ruleset[]>;
}

export interface RulesetRouteParams {
  params: {
    rulesetID?: string | undefined;
    propertyID?: string | undefined;
    groupID?: string | undefined;
    tenantID?: string | undefined;
  };
}

export interface RulesetContext {
  ruleset: Ruleset;
  setRuleset: (ruleset: Ruleset) => void;
  rulesets: Ruleset[];
  onBack: () => void;
  tenantRules: Rule[];
  formMode: FormMode;
  displayMode: DisplayMode;
  fetcherKey: string;
}

export const rulesetsLoader = ({
  params,
}: RulesetRouteParams): RulesetLoaderData => {
  if (params.propertyID) {
    return {
      _tenantRules: getTenantRules(Number(params.tenantID)),
      _rulesets: getPropertyRulesets({
        tenantID: params.tenantID,
        propertyID: params.propertyID,
      }),
    };
  } else if (params.groupID) {
    return {
      _tenantRules: getTenantRules(Number(params.tenantID)),
      _rulesets: getPropertyGroupRulesets({
        tenantID: params.tenantID,
        groupID: params.groupID,
      }),
    };
  } else if (params.tenantID) {
    return {
      _tenantRules: getTenantRules(Number(params.tenantID)),
      _rulesets: getTenantRulesets(Number(params.tenantID)),
    };
  } else {
    return {
      _tenantRules: Promise.resolve([]),
      _rulesets: Promise.resolve([]),
    };
  }
};

export const RulesetsPanel = (): ReactNode => {
  const { t } = useTranslation();
  const { breakpoints } = useTheme();
  const { state, dispatch } = useContext(AppContext);
  const displayToast = useContext(ToastContext);
  const { pathname } = useLocation();
  const { rulesetID } = useParams() as RulesetRouteParams['params'];
  const { fetcherKey } = useFetcherKey();
  const fetcher = useFetcher({ key: fetcherKey });
  const navigate = useNavigate();
  const mobile = useMediaQuery(breakpoints.down('lg'));
  const { entity, displayMode, formMode, setFormMode } =
    useOutletContext<PanelRootContext<Property | PropertyGroup | Tenant>>();
  const { _tenantRules, _rulesets } = useLoaderData() as RulesetLoaderData;
  const [rulesets, setRulesets] = useState<Ruleset[]>([]);
  const [ruleset, setRuleset] = useState<Ruleset>(
    getBlankRuleset(state.tenant.id),
  );
  const [tenantRules, setTenantRules] = useState<Rule[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [sorting, setSorting] = useState<MRT_SortingState>([]);

  const canAddRemove = useMemo(() => {
    if (AuthService.IsSuperAdmin() && displayMode !== 'tenant') {
      return true;
    }
    if (displayMode === 'property') {
      return state.entityPermissions.property.update;
    }
    if (displayMode === 'group') {
      return state.entityPermissions.property_group.update;
    }
    return false;
  }, [displayMode]);
  const canCreate = state.entityPermissions[EntityTypes.Rule].create;

  useEffect(() => {
    dispatch({
      type: ACTION_TYPE.SET_HEADER_TEXT,
      payload: t('ruleset.label_plural', { ns: 'entities' }),
    });
  }, [entity]);

  // await data fetch
  useEffect(() => {
    const awaitLoaderData = async () => {
      const [tr, rs] = await Promise.all([_tenantRules, _rulesets]);
      setRulesets(rs);
      setTenantRules(tr);
      if (rulesetID) {
        const _ruleset = rulesets.find((r) => r.id === Number(rulesetID));
        if (_ruleset) {
          setRuleset(_ruleset);
        }
      }
      setIsLoading(false);
    };
    awaitLoaderData();
  }, [_rulesets, _tenantRules, rulesetID, rulesets]);

  useEffect(() => {
    if (formMode === 'create') {
      setRuleset(getBlankRuleset(state.tenant.id));
    }
  }, [formMode]);

  // action response handling
  const getSuccessMessage = useCallback(
    (res: TraxeroActionResponse<EntityPanelActionHandler>): string => {
      switch (res.handler) {
        case EntityPanelActionHandler.UPDATE:
          return t('update_success', {
            name: `${t('ruleset.label', { ns: 'entities' })} ${ruleset?.name}`,
            ns: 'toasts',
          });
        case EntityPanelActionHandler.PROPERTY_CREATE:
        case EntityPanelActionHandler.GROUP_CREATE:
        case EntityPanelActionHandler.TENANT_CREATE:
          return t('create_success', {
            name: `${t('ruleset.label', { ns: 'entities' })} ${ruleset?.name}`,
            ns: 'toasts',
          });
        case EntityPanelActionHandler.PROPERTY_UPSERT:
          return t('update_success', {
            name: `${t('property.label', { ns: 'entities' })} ${ruleset?.name}`,
            ns: 'toasts',
          });
        case EntityPanelActionHandler.GROUP_UPSERT:
          return t('update_success', {
            name: `${t('property_group.label', {
              ns: 'entities',
            })} ${ruleset?.name}`,
            ns: 'toasts',
          });
        case EntityPanelActionHandler.DELETE:
          return t('delete_success', {
            name: `${t('ruleset.label', { ns: 'entities' })} ${
              res.body?.name || ''
            }`,
            ns: 'toasts',
          });
        default:
          return '';
      }
    },
    [],
  );
  const getErrorMessage = useCallback(
    (res: TraxeroActionResponse<EntityPanelActionHandler>): string => {
      const { handler, status, statusText } = res;
      switch (handler) {
        case EntityPanelActionHandler.UPDATE:
          return t('update_error', {
            name: `${t('ruleset.label', { ns: 'entities' })}`,
            error: `${status} - ${statusText}`,
            ns: 'toasts',
          });
        case EntityPanelActionHandler.PROPERTY_CREATE:
        case EntityPanelActionHandler.GROUP_CREATE:
        case EntityPanelActionHandler.TENANT_CREATE:
          return t('create_error', {
            name: `${t('ruleset.label', { ns: 'entities' })}`,
            error: `${status} - ${statusText}`,
            ns: 'toasts',
          });
        case EntityPanelActionHandler.PROPERTY_UPSERT:
          return t('update_error', {
            name: `${t('property.label', { ns: 'entities' })}${t(
              'ruleset.label_plural',
              { ns: 'entities' },
            )}`,
            error: `${status} - ${statusText}`,
            ns: 'toasts',
          });
        case EntityPanelActionHandler.GROUP_UPSERT:
          return t('update_error', {
            name: `${t('property_group.label', { ns: 'entities' })}${t(
              'ruleset.label_plural',
              { ns: 'entities' },
            )}`,
            error: `${status} - ${statusText}`,
            ns: 'toasts',
          });
        case EntityPanelActionHandler.DELETE:
          return t('delete_error', {
            name: `${t('ruleset.label', { ns: 'entities' })}`,
            error: `${status} - ${statusText}`,
            ns: 'toasts',
          });
        default:
          return t('unknown_error', { ns: 'toasts' });
      }
    },
    [],
  );
  const response =
    fetcher.data as TraxeroActionResponse<EntityPanelActionHandler> | null;
  useEffect(() => {
    if (response) {
      if (response.status < 400) {
        displayToast({
          type: TOAST_TYPE.SUCCESS,
          message: getSuccessMessage(response),
        });
        if (response.handler !== EntityPanelActionHandler.DELETE) {
          handleCancelAction();
        }
      } else {
        displayToast({
          type: TOAST_TYPE.ERROR,
          message: getErrorMessage(response),
        });
      }
    }
  }, [fetcher]);

  // button and menu handlers
  const handleRemoveRuleset = (id: number) => {
    const newRulesets: Ruleset[] = rulesets.filter((r) => r.id !== id) || [];
    fetcher.submit(
      {
        body: JSON.stringify(newRulesets),
        handler: getHandler({ displayMode, formMode }),
      },
      {
        method: 'PUT',
        encType: 'application/json',
      },
    );
  };
  const handleCancelAction = useCallback(() => {
    setFormMode('none');
    setRuleset(getBlankRuleset(state.tenant.id));
    // resolve the list route explicitly -- reading the current pathname here
    // would point at the form route the user is trying to leave
    navigate(
      routeWithParams(getActionRouteForDisplayMode(displayMode), [
        state.tenant.id,
        entity?.id,
      ]),
      { replace: true },
    );
  }, [displayMode, state.tenant.id, entity?.id, navigate]);
  const handleViewRevisions = (id: number) => {
    navigate(
      routeWithParams(ROUTES.REVISIONS, [
        state.tenant.id,
        EntityTypes.Ruleset,
        id.toString(),
      ]),
    );
  };
  const handleRulesetClick = (id: number) => {
    const _ruleset = rulesets.find((r) => r.id === id);
    if (_ruleset) {
      setFormMode('edit');
      setRuleset(_ruleset);
    } else {
      displayToast({
        type: TOAST_TYPE.ERROR,
        message: t('find_error', {
          name: t('ruleset.label', { ns: 'entities' }),
          error: t('errors.notFound'),
          ns: 'toasts',
        }),
      });
      return;
    }

    if (displayMode === 'tenant') {
      setFormMode('edit');
      navigate(routeWithParams(ROUTES.RULESETS_FORM, [state.tenant.id, id]));
    } else {
      navigate(
        routeWithParams(getFormRouteForDisplayMode(displayMode), [
          state.tenant.id,
          entity?.id,
          id,
        ]),
      );
    }
  };
  const handleAddRuleset = (r: Ruleset | null) => {
    if (r) {
      const updatedRulesets = [...rulesets, r];
      fetcher.submit(
        {
          handler: getHandler({ displayMode, formMode }),
          body: JSON.stringify(updatedRulesets),
        },
        {
          method: 'PUT',
          action: pathname,
          encType: 'application/json',
        },
      );
    }
  };
  const handleCreateClick = () => {
    setFormMode('create');
    setRuleset(getBlankRuleset(state.tenant.id));
    navigate(`${pathname}/0`);
  };

  const handleSoftDelete = (ruleset: Ruleset): void => {
    const updatedRuleset = { ...ruleset, status: Status.Disabled };
    fetcher.submit(
      {
        handler: EntityPanelActionHandler.DELETE,
        body: JSON.stringify(updatedRuleset),
      },
      {
        method: 'PUT',
        action: pathname,
        encType: 'application/json',
      },
    );
  };

  // data display
  const columns = useMemo<MRT_ColumnDef<Ruleset>[]>(() => {
    const _columns: MRT_ColumnDef<Ruleset>[] = [
      {
        header: t('ruleset.id', { ns: 'entities' }),
        accessorKey: 'id',
      },
      {
        header: t('ruleset.name', { ns: 'entities' }),
        accessorKey: 'name',
      },
      {
        header: t('ruleset.description', { ns: 'entities' }),
        accessorKey: 'description',
      },
      {
        header: t('rule.label_plural', { ns: 'entities' }),
        accessorFn: (row) => row.rules.length,
        accessorKey: 'ruleCount',
        filterVariant: 'range',
      },
      {
        header: t('rule.status', { ns: 'entities' }),
        accessorKey: 'status',
        filterVariant: 'select',
        filterSelectOptions: [
          {
            label: t('status.active', { ns: 'enums' }),
            value: Status.Active,
          },
          {
            label: t('status.disabled', { ns: 'enums' }),
            value: Status.Disabled,
          },
        ],
        id: 'status',
        Cell: ({ row }: { row: MRT_Row<Ruleset> }) => {
          return capitalize(row.original.status);
        },
      },
      {
        header: t('ruleset.created', { ns: 'entities' }),
        id: 'created',
        accessorFn: (row) => (row.created ? new Date(row.created) : null),
        Cell: ({ cell }) =>
          cell.getValue<Date>()
            ? dayjs(cell.getValue<Date>()).format('MM/DD/YYYY')
            : '',
        filterVariant: 'date',
        filterFn: (row, id, filterValue) => {
          return dayjs(row.getValue(id)).isSame(dayjs(filterValue), 'day');
        },
      },
      {
        header: t('ruleset.updated', { ns: 'entities' }),
        id: 'updated',
        accessorFn: (row) => (row.updated ? new Date(row.updated) : null),
        Cell: ({ cell }) =>
          cell.getValue<Date>()
            ? dayjs(cell.getValue<Date>()).format('MM/DD/YYYY')
            : '',
        filterVariant: 'date',
        filterFn: (row, id, filterValue) => {
          return dayjs(row.getValue(id)).isSame(dayjs(filterValue), 'day');
        },
      },
    ];

    if (displayMode !== 'tenant') {
      _columns.push({
        header: t('from', {
          name: t('property_group.label', { ns: 'entities' }),
        }),
        accessorKey: 'ancestry.propertyGroupName',
        id: 'propertyGroupName',
      });
    }

    return _columns;
  }, [displayMode]);

  // desktop table
  const tableDefaults = useDefaultTableOptions<Ruleset>(TableType.NESTED);
  const table = useMaterialReactTable({
    ...tableDefaults,
    data: rulesets,
    columns,
    enableRowSelection: false,
    initialState: {
      showColumnFilters: false,
      showGlobalFilter: true,
      density: 'compact',
      columnVisibility: {
        id: false,
        name: true,
        description: true,
        ruleCount: true,
        status: true,
        created: false,
        updated: false,
        propertyGroupName: false,
      },
      columnPinning: {
        right: ['mrt-row-actions'],
      },
    },
    state: {
      isLoading,
      sorting,
      showLoadingOverlay: false,
    },
    onSortingChange: setSorting,
    getRowId: (row) => `${row.id}`,
    muiTableBodyRowProps: ({ row }) => ({
      sx: {
        backgroundColor: 'white',
        cursor: 'pointer',
      },
      onClick: () => handleRulesetClick(row.original.id),
    }),
    renderTopToolbar: ({ table }) => (
      <MRTableToolbar
        table={table}
        isSubmitting={fetcher.state === 'submitting'}
        optionsDisabled={!canCreate}
        tableType={TableType.NESTED}
        menuOptions={[
          {
            onClick: () => handleCreateClick(),
            content: t('buttons.create', {
              name: t('ruleset.label', { ns: 'entities' }),
            }),
            disabled: !canCreate,
            icon: <Add />,
          },
        ]}
      >
        <Stack
          direction={'row'}
          flexGrow={1}
          justifyContent={
            displayMode === 'tenant' ? 'space-between' : 'flex-start'
          }
          alignItems={'center'}
        >
          <Typography
            variant='h3'
            textTransform={'capitalize'}
            sx={{ flex: 1 }}
          >
            {t('ruleset.label_plural', { ns: 'entities' })}
          </Typography>
          {displayMode !== 'tenant' && (
            <EntityLookup
              entityType={EntityTypes.Ruleset}
              onSelect={handleAddRuleset}
              renderOption={(option: Ruleset | null) => {
                if (!option) return null;
                return (
                  <React.Fragment>
                    <Typography variant={'body2'} noWrap fontWeight={'bold'}>
                      {`${option.name}`}
                    </Typography>
                    <Typography variant={'body2'} noWrap>
                      {`${option.description}`}
                    </Typography>
                  </React.Fragment>
                );
              }}
              size='small'
              sx={{ width: '30%' }}
            />
          )}
        </Stack>
      </MRTableToolbar>
    ),
    renderRowActionMenuItems: ({ closeMenu, row }) => {
      const options = [
        <MenuItem
          key={1}
          divider
          onClick={() => {
            handleRemoveRuleset(row.original.id);
            closeMenu();
          }}
          sx={{ m: 0, backgroundColor: 'white' }}
          disabled={displayMode === 'tenant'}
        >
          <ListItemIcon>
            <Delete />
          </ListItemIcon>
          {t('buttons.remove', {
            name: t('ruleset.label', { ns: 'entities' }),
          })}
        </MenuItem>,
        <MenuItem
          key={2}
          divider
          onClick={() => {
            handleViewRevisions(row.original.id);
            closeMenu();
          }}
          sx={{ m: 0, backgroundColor: 'white' }}
        >
          <ListItemIcon>
            <History />
          </ListItemIcon>
          {t('buttons.view', { name: t('revision.label_plural') })}
        </MenuItem>,
      ];
      if (AuthService.IsSuperAdmin()) {
        options.push(
          <MenuItem
            key={3}
            onClick={() => {
              handleSoftDelete(row.original);
              closeMenu();
            }}
            sx={{
              m: 0,
              backgroundColor: 'warning.main',
              color: 'white',
              '&:hover': {
                backgroundColor: 'warning.dark',
              },
            }}
          >
            <ListItemIcon>
              <Warning sx={{ color: 'white' }} />
            </ListItemIcon>
            {t('buttons.delete', {
              name: t('ruleset.label', { ns: 'entities' }),
            })}
          </MenuItem>,
        );
      }
      return options;
    },
  });

  if (formMode !== 'none') {
    return (
      <Outlet
        context={
          {
            ruleset,
            setRuleset,
            rulesets,
            tenantRules,
            onBack: handleCancelAction,
            formMode,
            displayMode,
            fetcherKey,
          } satisfies RulesetContext
        }
      />
    );
  }

  // mobile list
  if (mobile) {
    const itemHeight = 70;
    const renderItem = (item: Ruleset): ReactNode => (
      <ListItem
        key={item.id}
        id={item.id.toString()}
        onClick={() => handleRulesetClick(item.id)}
        divider
        secondaryAction={
          <ListItemMenuButton
            menuOptions={[
              {
                content: t('buttons.remove', { name: t('ruleset.label', { ns: 'entities' }) }),
                onClick: () => handleRemoveRuleset(item.id),
                disabled: !canAddRemove,
              },
              {
                content: t('buttons.view', {
                  name: t('revision.label_plural'),
                }),
                onClick: () => handleViewRevisions(item.id),
              },
            ]}
          />
        }
        sx={{ height: itemHeight, backgroundColor: 'white' }}
      >
        <ListItemText
          primary={item.name}
          secondary={item.description}
          primaryTypographyProps={{
            noWrap: true,
            variant: 'subtitle1',
          }}
          secondaryTypographyProps={{
            noWrap: true,
            variant: 'subtitle2',
          }}
        />
      </ListItem>
    );

    return (
      <MobilePanelList<Ruleset>
        data={rulesets}
        columns={columns.map((c) => ({
          name: c.header,
          searchKey: c.accessorKey,
        }))}
        itemHeight={itemHeight}
        renderItem={renderItem}
        title={t('ruleset.label_plural', { ns: 'entities' })}
        withSearchBar
        heightOffset={TAB_LAYOUT_HEADER_HEIGHT}
        menuOptions={[
          {
            onClick: () => handleCreateClick(),
            content: t('buttons.create', { name: t('ruleset.label', { ns: 'entities' }) }),
            disabled: !canCreate,
          },
        ]}
      />
    );
  }

  return (
    <FormFrame loading={false}>
      <MaterialReactTable table={table} />
    </FormFrame>
  );
};

export const getActionRouteForDisplayMode = (
  displayMode: DisplayMode,
): ROUTES => {
  switch (displayMode) {
    case 'property':
      return ROUTES.PROPERTY_RULESETS;
    case 'group':
      return ROUTES.PROPERTY_GROUP_RULESETS;
    case 'tenant':
      return ROUTES.RULESETS;
    default:
      return ROUTES.PROPERTY_RULESETS;
  }
};
export const getFormRouteForDisplayMode = (
  displayMode: DisplayMode,
): ROUTES => {
  switch (displayMode) {
    case 'property':
      return ROUTES.PROPERTY_RULESETS_FORM;
    case 'group':
      return ROUTES.PROPERTY_GROUP_RULESETS_FORM;
    case 'tenant':
      return ROUTES.RULESETS_FORM;
    default:
      return ROUTES.INDEX;
  }
};
interface GetHandlerParams {
  displayMode: DisplayMode;
  formMode: FormMode;
}
export const getHandler = ({
  displayMode,
  formMode,
}: GetHandlerParams): EntityPanelActionHandler => {
  switch (displayMode) {
    case 'property':
      return formMode === 'create'
        ? EntityPanelActionHandler.PROPERTY_CREATE
        : EntityPanelActionHandler.PROPERTY_UPSERT;
    case 'group':
      return formMode === 'create'
        ? EntityPanelActionHandler.GROUP_CREATE
        : EntityPanelActionHandler.GROUP_UPSERT;
    case 'tenant':
      return EntityPanelActionHandler.TENANT_CREATE;
    default:
      return EntityPanelActionHandler.TENANT_CREATE;
  }
};

export const rulesetsAction: ActionFunction = async ({
  params,
  request,
}): Promise<TraxeroActionResponse<EntityPanelActionHandler>> => {
  const { tenantID, groupID, propertyID, rulesetID } =
    params as RulesetRouteParams['params'];
  const { body, handler } = await request.json();
  let res: Response;
  let rulesetData: Ruleset | null = null;
  try {
    switch (handler) {
      case EntityPanelActionHandler.UPDATE:
        res = await updateRuleset({
          rulesetID,
          tenantID,
          body,
        });
        break;
      case EntityPanelActionHandler.TENANT_CREATE:
        res = await createRuleset({
          tenantID,
          body,
        });
        break;
      case EntityPanelActionHandler.DELETE: {
        const ruleset = JSON.parse(body) as Ruleset;
        rulesetData = ruleset;
        res = await updateRuleset({
          tenantID,
          rulesetID: ruleset.id,
          body,
        });
        break;
      }
      case EntityPanelActionHandler.PROPERTY_CREATE:
      case EntityPanelActionHandler.PROPERTY_UPSERT:
        res = await putPropertyRulesets({
          tenantID,
          propertyID,
          body,
        });
        break;
      case EntityPanelActionHandler.GROUP_CREATE:
      case EntityPanelActionHandler.GROUP_UPSERT:
        res = await putPropertyGroupRulesets({
          tenantID,
          groupID,
          body,
        });
        break;
      default: {
        res = new Response(null, { status: 400, statusText: 'Bad Request' });
      }
    }
  } catch (e) {
    res = new Response(null, {
      status: 500,
      statusText: 'Internal Server Error',
    });
  }

  return {
    handler,
    status: res.status,
    statusText: res.statusText,
    body: rulesetData,
  };
};

export * from './RulesetForm';
